import React, { useState, useEffect, useRef } from 'react';
import { Radio, Checkbox, Upload, message, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import ErrorModal from '../../../components/ErrorModal';
import { submitSurvey, uploadFile, saveVerifyFiles, submitVerifyMaterials } from '../../../api';
import { getApiErrorMessage } from '../../../utils/apiErrorUtils';
import { useEnterprise } from '../../../contexts/EnterpriseContext';
import { questionnaireNoticeData } from '../../../mockData/questionnaireNotice';
import './index.css';
import './index.mobile.css';

const FFQ_NOTICE_SEEN_KEY = 'ffq_notice_seen';

const MODULE_STATUS_LABEL = {
  done: 'ff.questionnaire.status.done',
  doing: 'ff.questionnaire.status.doing',
  undone: 'ff.questionnaire.status.undone',
};

const VALIDATION_MODULE_STATUS_LABEL = {
  not_picked: 'ff.questionnaire.validation.module.not_picked',
  pending: 'ff.questionnaire.validation.module.pending',
  uploaded: 'ff.questionnaire.validation.module.uploaded',
  passed: 'ff.questionnaire.validation.module.passed',
};

const REJECT_TYPE_KEYS = ['incomplete', 'irrelevant', 'unclear', 'other'];

export default function Questionnaire() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    enterprise,
    surveyProgress: contextSurveyProgress,
    validationStatus: contextValidationStatus,
    surveyPaper: contextSurveyPaper,
    savedAnswers: contextSavedAnswers,
    verifyPaper: contextVerifyPaper,
    setValidationStatus,
    refreshProgress,
  } = useEnterprise() || {};

  const [modules, setModules] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [currentModule, setCurrentModule] = useState(null);
  const [currentQ, setCurrentQ] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const loading = !contextSurveyPaper || !enterprise;

  const [showIncompleteModal, setShowIncompleteModal] = useState(false);
  const [incompleteFields, setIncompleteFields] = useState([]);
  const [showNoticeModal, setShowNoticeModal] = useState(false);

  // ── Validation mode state ──────────────────────────────────────────────────
  const [isValidationMode, setIsValidationMode] = useState(false);
  const [validationData, setValidationData] = useState(null);
  // { validationStatus, totalDrawn, uploadedCount, drawnQuestionIds, validationStatuses, savedAnswers }
  const [validationAnswers, setValidationAnswers] = useState({});
  // { [questionId]: { textEvidence: '', remark: '', files: [] } }
  const [showValidationFailModal, setShowValidationFailModal] = useState(false);
  const [showValidationIncompleteModal, setShowValidationIncompleteModal] = useState(false);
  const [validationIncompleteFields, setValidationIncompleteFields] = useState([]);
  const [isValidationSubmitting, setIsValidationSubmitting] = useState(false);
  const [isValidationSaving, setIsValidationSaving] = useState(false);

  const initializedRef = useRef(false);
  const lastVerifyPaperRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const autoSaveValidationTimerRef = useRef(null);
  const isAutoSavingRef = useRef(false);
  const isAutoSavingValidationRef = useRef(false);
  const userMadeAnswerChangeRef = useRef(false);
  const userMadeValidationChangeRef = useRef(false);

  const isFillingState =
    contextSurveyProgress !== null &&
    contextSurveyProgress < 2 &&
    (contextValidationStatus ?? 0) < 10;

  useEffect(() => {
    if (!contextSurveyPaper || !enterprise || !isFillingState) return;
    if (!localStorage.getItem(FFQ_NOTICE_SEEN_KEY)) {
      setShowNoticeModal(true);
      localStorage.setItem(FFQ_NOTICE_SEEN_KEY, '1');
    }
  }, [contextSurveyPaper, enterprise, isFillingState]);

  useEffect(() => {
    if (!contextSurveyPaper || !enterprise) return;

    const mods = contextSurveyPaper.modules || [];
    setModules(mods);
    const allQuestions = mods.flatMap((m) =>
      (m.questions || []).map((q) => ({ ...q, moduleName: m.name, moduleId: m.id }))
    );
    setQuestions(allQuestions);

    if (!initializedRef.current) {
      if (mods.length) setCurrentModule(mods[0].id);
      if (allQuestions.length) setCurrentQ(allQuestions[0].id);

      const answerMap = {};
      (contextSavedAnswers || []).forEach((a) => {
        answerMap[a.questionId] = {
          optionId: a.optionId,
          optionIds: a.optionIds || [],
          textAnswer: a.textAnswer,
        };
      });
      setAnswers(answerMap);
      setSubmitted(contextSurveyProgress === 2);

      initializedRef.current = true;
    }

    // Validation mode: initialize whenever verifyPaper changes (re-fetch after save)
    const vs = contextValidationStatus ?? 0;
    if (
      vs >= 10 &&
      allQuestions.length &&
      contextVerifyPaper &&
      contextVerifyPaper !== lastVerifyPaperRef.current
    ) {
      lastVerifyPaperRef.current = contextVerifyPaper;
      const verifyResult = contextVerifyPaper;
      const currentRound = verifyResult?.currentRound ?? 1;
      const items = verifyResult?.items || [];
      const editable = verifyResult?.editable ?? false;

      const fieldMap = {};
      const initialValidationAnswers = {};

      items.forEach((item) => {
        const currentRoundFiles =
          (item.fileRounds || []).find((r) => r.round === currentRound)?.files || [];
        const currentRoundRemark = item[`round${currentRound}Remark`] || '';

        const status = item.materialStatus;
        const validationStatus =
          status === 'passed'
            ? 'passed'
            : currentRoundFiles.length > 0 || status === 'in_review'
              ? 'uploaded'
              : 'pending';

        const prevFiles = (item.fileRounds || [])
          .filter((r) => r.round < currentRound)
          .flatMap((r) => r.files || [])
          .map((f) => ({ uid: f.fileId, name: f.fileName || '', type: '', url: f.fileUrl || '' }));

        fieldMap[item.questionId] = {
          validationStatus,
          materialRequirement: item.materialRequirement || '',
          failReasonType: item.round1FailReasonType || null,
          failReason: item.round1Feedback || null,
          previousFiles: prevFiles.length > 0 ? prevFiles : undefined,
        };

        if (currentRoundFiles.length > 0 || currentRoundRemark) {
          initialValidationAnswers[item.questionId] = {
            textEvidence: '',
            remark: currentRoundRemark,
            files: currentRoundFiles.map((f) => ({
              uid: f.fileId,
              name: f.fileName || '',
              status: 'done',
              url: f.fileUrl || '',
            })),
          };
        }
      });

      setValidationData({
        totalDrawn: items.length,
        fieldMap,
        editable,
        verifyStatus: vs,
        currentRound,
      });
      setValidationAnswers(initialValidationAnswers);
      setIsValidationMode(true);
      if (setValidationStatus) setValidationStatus(vs);
    }
  }, [contextSurveyPaper, contextVerifyPaper, enterprise]); // eslint-disable-line react-hooks/exhaustive-deps

  const isVisible = (q) => {
    const deps = Array.isArray(q.visibleWhenOptionIds) ? q.visibleWhenOptionIds : [];
    if (!deps.length) return true;
    return deps.some((optId) =>
      Object.values(answers).some(
        (a) => a.optionId === optId || (Array.isArray(a.optionIds) && a.optionIds.includes(optId))
      )
    );
  };

  const visibleQuestions = questions.filter(isVisible);
  const currentModuleObj = modules.find((m) => m.id === currentModule);
  const moduleQuestions = questions.filter((q) => q.moduleId === currentModule);
  const visibleModuleQuestions = moduleQuestions.filter(isVisible);
  const currentQuestion = visibleQuestions.find((q) => q.id === currentQ);
  const globalIndex = visibleQuestions.findIndex((q) => q.id === currentQ);
  const moduleQIndex = visibleModuleQuestions.findIndex((q) => q.id === currentQ);

  const isAnswered = (q) => {
    const a = answers[q.id];
    if (!a) return false;
    if (q.questionType === 3 || q.questionType === 9)
      return Array.isArray(a.optionIds) && a.optionIds.length > 0;
    return !!(a.optionId || a.textAnswer);
  };
  const answeredCount = visibleQuestions.filter(isAnswered).length;

  const moduleStatuses = modules.map((m) => {
    const mqs = visibleQuestions.filter((q) => q.moduleId === m.id);
    const answeredAll = mqs.length > 0 && mqs.every((q) => isAnswered(q));
    const answeredSome = mqs.some((q) => isAnswered(q));
    return {
      ...m,
      key: m.id,
      label: m.name,
      status: answeredAll ? 'done' : answeredSome ? 'doing' : 'undone',
    };
  });

  // ── Validation helpers ─────────────────────────────────────────────────────

  const fieldMap = validationData?.fieldMap || {};

  const getValidationField = (questionId) =>
    fieldMap[questionId] || { validationStatus: 'notDrawn' };

  const validationUploadedCount = isValidationMode
    ? Object.values(fieldMap).filter((f) => ['uploaded', 'passed'].includes(f.validationStatus))
        .length
    : 0;

  const validationModuleStatuses = isValidationMode
    ? modules.map((m) => {
        const mqs = questions.filter((q) => q.moduleId === m.id);
        const drawnQs = mqs.filter((q) => fieldMap[q.id]);
        if (drawnQs.length === 0) return { ...m, key: m.id, label: m.name, status: 'not_picked' };
        const getEffStatus = (qId) => fieldMap[qId]?.validationStatus ?? 'pending';
        if (drawnQs.every((q) => ['uploaded', 'passed'].includes(getEffStatus(q.id))))
          return { ...m, key: m.id, label: m.name, status: 'uploaded' };
        return { ...m, key: m.id, label: m.name, status: 'pending' };
      })
    : null;

  const updateValidationAnswer = (questionId, field, value) => {
    userMadeValidationChangeRef.current = true;
    setValidationAnswers((prev) => ({
      ...prev,
      [questionId]: {
        ...(prev[questionId] || { textEvidence: '', remark: '', files: [] }),
        [field]: value,
      },
    }));

    if (field === 'files') {
      const hasDoneFiles = value.some((f) => f.status === 'done');
      setValidationData((prev) => {
        const currentStatus = prev.fieldMap[questionId]?.validationStatus;
        if (currentStatus === 'passed') return prev;
        const newFieldMap = {
          ...prev.fieldMap,
          [questionId]: {
            ...prev.fieldMap[questionId],
            validationStatus: hasDoneFiles ? 'uploaded' : 'pending',
          },
        };
        return { ...prev, fieldMap: newFieldMap };
      });
    }
  };

  // ── Auto-save ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!userMadeAnswerChangeRef.current) return;
    if (submitted || !enterprise?.id) return;

    clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => {
      if (isAutoSavingRef.current) return;
      isAutoSavingRef.current = true;
      try {
        await submitSurvey(enterprise.id, 1, buildAnswerList());
      } catch {
        // silent
      } finally {
        isAutoSavingRef.current = false;
      }
    }, 1500);

    return () => clearTimeout(autoSaveTimerRef.current);
  }, [answers]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!userMadeValidationChangeRef.current) return;
    if (!isValidationMode || validationData?.editable === false) return;

    const hasUploading = Object.values(validationAnswers).some((a) =>
      (a.files || []).some((f) => f.status === 'uploading')
    );
    if (hasUploading) return;

    clearTimeout(autoSaveValidationTimerRef.current);
    autoSaveValidationTimerRef.current = setTimeout(async () => {
      if (isAutoSavingValidationRef.current) return;
      isAutoSavingValidationRef.current = true;
      try {
        await saveVerifyFiles(buildSaveItems());
      } catch {
        // silent
      } finally {
        isAutoSavingValidationRef.current = false;
      }
    }, 1500);

    return () => clearTimeout(autoSaveValidationTimerRef.current);
  }, [validationAnswers]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Navigation ─────────────────────────────────────────────────────────────

  const navigateTo = (q) => {
    if (!q) return;
    setCurrentQ(q.id);
    if (q.moduleId !== currentModule) setCurrentModule(q.moduleId);
  };

  useEffect(() => {
    if (!currentQ || !questions.length) return;
    const q = questions.find((item) => item.id === currentQ);
    if (q && !isVisible(q)) {
      const idx = visibleQuestions.findIndex((vq) => vq.id === currentQ);
      const target = visibleQuestions[idx - 1] ?? visibleQuestions[0];
      if (target) navigateTo(target);
    }
  }, [answers]); // eslint-disable-line react-hooks/exhaustive-deps

  const setAnswerWithClear = (updater) => {
    userMadeAnswerChangeRef.current = true;
    setAnswers((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      const cleaned = { ...next };
      questions.forEach((q) => {
        if (
          Array.isArray(q.visibleWhenOptionIds) &&
          q.visibleWhenOptionIds.length &&
          cleaned[q.id]
        ) {
          const visible = q.visibleWhenOptionIds.some((optId) =>
            Object.values(cleaned).some(
              (a) =>
                a.optionId === optId || (Array.isArray(a.optionIds) && a.optionIds.includes(optId))
            )
          );
          if (!visible) delete cleaned[q.id];
        }
      });
      return cleaned;
    });
  };

  const buildAnswerList = () =>
    Object.entries(answers).map(([questionId, val]) => ({
      questionId,
      optionId: val.optionId || null,
      optionIds: val.optionIds?.length ? val.optionIds : null,
      textAnswer: val.textAnswer || null,
    }));

  // ── Normal questionnaire handlers ──────────────────────────────────────────

  const handleSaveDraft = async () => {
    if (!enterprise?.id) return;
    message.config({ top: 100 });
    try {
      await submitSurvey(enterprise.id, 1, buildAnswerList());
      message.success(t('ff.questionnaire.saveDraftSuccess'));
    } catch {
      message.error(t('ff.questionnaire.saveDraftError'));
    }
  };

  const handleSubmit = async () => {
    const incomplete = [
      ...new Set(
        visibleQuestions
          .filter((q) => q.required === true && !isAnswered(q))
          .map((q) => q.moduleName)
      ),
    ];
    if (incomplete.length > 0) {
      setIncompleteFields(incomplete);
      setShowIncompleteModal(true);
      return;
    }
    if (!enterprise?.id) return;
    try {
      await submitSurvey(enterprise.id, 2, buildAnswerList());
      setSubmitted(true);
      refreshProgress?.();
      navigate('/family-friendly-submit-success?type=questionnaire');
    } catch (err) {
      setIncompleteFields([getApiErrorMessage(err.message, t, 'ff.questionnaire.submitError')]);
      setShowIncompleteModal(true);
    }
  };

  // ── Validation handlers ────────────────────────────────────────────────────

  const buildSaveItems = () =>
    Object.keys(fieldMap).map((questionId) => {
      const ans = validationAnswers[questionId] || {};
      return {
        questionId,
        files: (ans.files || []).filter((f) => f.status === 'done'),
        remark: ans.remark || '',
      };
    });

  const handleValidationSaveDraft = async () => {
    if (isValidationSaving) return;
    setIsValidationSaving(true);
    message.config({ top: 100 });
    try {
      const items = buildSaveItems();
      await saveVerifyFiles(items);
      message.success(t('ff.questionnaire.validation.saveDraftSuccess'));
    } catch {
      message.error(t('ff.questionnaire.validation.saveDraftError'));
    } finally {
      setIsValidationSaving(false);
    }
  };

  const handleValidationSubmit = async () => {
    if (isValidationSubmitting) return;

    const isSecondRoundOrLater = (validationData?.currentRound ?? 1) >= 2;

    // Round 1: all drawn questions must have at least one uploaded file.
    // Round 2+: materials are optional and empty submissions are allowed.
    if (!isSecondRoundOrLater) {
      const drawnQuestions = questions.filter((q) => fieldMap[q.id]);
      const incompleteModuleNames = [
        ...new Set(
          drawnQuestions
            .filter((q) => {
              const hasFile = (validationAnswers[q.id]?.files || []).some(
                (f) => f.status === 'done'
              );
              const alreadyUploaded = ['uploaded', 'passed'].includes(
                fieldMap[q.id]?.validationStatus
              );
              return !hasFile && !alreadyUploaded;
            })
            .map((q) => q.moduleName)
            .filter(Boolean)
        ),
      ];
      if (incompleteModuleNames.length > 0) {
        setValidationIncompleteFields(incompleteModuleNames);
        setShowValidationIncompleteModal(true);
        return;
      }
    }

    setIsValidationSubmitting(true);
    try {
      // Save all pending file changes first, then submit
      const saveItems = buildSaveItems();
      await saveVerifyFiles(saveItems);
      await submitVerifyMaterials();
      refreshProgress?.();
      navigate('/family-friendly-submit-success?type=validation');
    } catch {
      setShowValidationFailModal(true);
    } finally {
      setIsValidationSubmitting(false);
    }
  };

  // ── File upload handler for validation ────────────────────────────────────

  const handleValidationUpload =
    (questionId) =>
    async ({ file, onSuccess, onError }) => {
      try {
        const res = await uploadFile(file);
        const url = res.result || '';
        onSuccess({ url }, file);
      } catch (err) {
        onError(err);
        message.error(t('ff.questionnaire.validation.saveDraftError'));
      }
    };

  const beforeUpload = (file) => {
    const validType =
      ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'].includes(file.type) ||
      /\.(jpg|jpeg|png|pdf)$/i.test(file.name);
    if (!validType) return Upload.LIST_IGNORE;
    if (file.size / 1024 / 1024 > 10) {
      message.error('文件大小不能超过 10 MB');
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  if (loading) {
    return (
      <div className="ffq-page">
        <h1 className="ffq-title">{t('ff.questionnaire.title')}</h1>
        <div className="ffq-card ffq-card--loading" />
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="ffq-page">
      <h1 className="ffq-title">
        {isValidationMode ? t('ff.questionnaire.validation.title') : t('ff.questionnaire.title')}
      </h1>

      {isValidationMode ? (
        <p className="ffq-subtitle">
          {t('ff.questionnaire.validation.subtitle', {
            count: validationData?.totalDrawn ?? 0,
          })}
          <button className="ffq-notice-entry" onClick={() => setShowNoticeModal(true)}>
            {t('ff.questionnaire.notice.entry')}
          </button>
          {t('ff.questionnaire.subtitle.post')}
        </p>
      ) : (
        <p className="ffq-subtitle">
          {t('ff.questionnaire.subtitle', { count: modules.length })}
          <button className="ffq-notice-entry" onClick={() => setShowNoticeModal(true)}>
            {t('ff.questionnaire.subtitle.link')}
          </button>
          {t('ff.questionnaire.subtitle.post')}
        </p>
      )}

      <div className="ffq-card">
        <div className="ffq-card-content">
          <div className="ffq-card-content-left">
            <div className="ffq-progress-header">
              {/* Validation status legend */}
              {isValidationMode && (
                <div className="ffq-progress-row ffq-progress-row--status">
                  <span className="ffq-section-label">
                    {t('ff.questionnaire.validation.statusLabel')}
                  </span>
                  <div className="ffq-status-legend">
                    <span className="ffq-status-dot ffq-status-dot--not-drawn" />
                    <span className="ffq-status-legend-label">
                      {t('ff.questionnaire.validation.status.notDrawn')}
                    </span>
                    <span className="ffq-status-dot ffq-status-dot--uploaded" />
                    <span className="ffq-status-legend-label">
                      {t('ff.questionnaire.validation.status.uploaded')}
                    </span>
                    <span className="ffq-status-dot ffq-status-dot--pending" />
                    <span className="ffq-status-legend-label">
                      {t('ff.questionnaire.validation.status.pending')}
                    </span>
                  </div>
                </div>
              )}

              {/* Module progress pills */}
              <div className="ffq-progress-row">
                <span className="ffq-section-label">{t('ff.questionnaire.progressLabel')}</span>
                <div className="ffq-modules-wrap">
                  {(isValidationMode ? validationModuleStatuses : moduleStatuses).map(
                    (mod, i, arr) => (
                      <React.Fragment key={mod.key}>
                        <div className="ffq-module-col">
                          <button
                            className={`ffq-module-pill ffq-module-pill--${mod.status} ${currentModule === mod.key ? 'ffq-module-pill--selected' : ''}`}
                            onClick={() => {
                              setCurrentModule(mod.key);
                              const first = questions.find((q) => q.moduleId === mod.key);
                              if (first) setCurrentQ(first.id);
                            }}
                            ref={(btn) => {
                              if (!btn) return;
                              const labelEl = btn.querySelector('.ffq-module-pill__label');
                              if (labelEl && labelEl.scrollWidth > labelEl.clientWidth) {
                                btn.setAttribute('data-label', mod.label);
                              } else {
                                btn.removeAttribute('data-label');
                              }
                            }}
                          >
                            <span className="ffq-module-pill__label">{mod.label}</span>
                          </button>
                          <span
                            className={`ffq-module-status-label ffq-module-status-label--${mod.status}`}
                          >
                            {t(
                              isValidationMode
                                ? VALIDATION_MODULE_STATUS_LABEL[mod.status]
                                : MODULE_STATUS_LABEL[mod.status]
                            )}
                          </span>
                        </div>
                        {i < arr.length - 1 && (
                          <div
                            className={`ffq-module-connector ${
                              isValidationMode
                                ? ['uploaded', 'passed'].includes(mod.status)
                                  ? 'ffq-module-connector--done'
                                  : ''
                                : mod.status === 'done'
                                  ? 'ffq-module-connector--done'
                                  : ''
                            }`}
                          />
                        )}
                      </React.Fragment>
                    )
                  )}
                </div>
              </div>

              {/* Answer card grid */}
              <div className="ffq-progress-row ffq-progress-row--card">
                <span className="ffq-section-label">{t('ff.questionnaire.answerCardLabel')}</span>
                <div className="ffq-answer-grid">
                  {visibleModuleQuestions.map((q, idx) => {
                    const vStatus = isValidationMode
                      ? getValidationField(q.id).validationStatus
                      : null;
                    let cellClass = 'ffq-answer-cell';
                    if (q.id === currentQ) cellClass += ' ffq-answer-cell--current';
                    if (isValidationMode) {
                      if (['uploaded', 'passed'].includes(vStatus))
                        cellClass += ' ffq-answer-cell--v-uploaded';
                      else if (vStatus === 'pending') cellClass += ' ffq-answer-cell--v-pending';
                      else cellClass += ' ffq-answer-cell--v-not-drawn';
                    } else if (isAnswered(q)) {
                      cellClass += ' ffq-answer-cell--done';
                    }
                    return (
                      <button key={q.id} className={cellClass} onClick={() => setCurrentQ(q.id)}>
                        {idx + 1}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {currentQuestion && (
              <div className="ffq-body">
                <div className="ffq-question-area">
                  {currentQuestion.type ? (
                    <div className="ffq-module-tag">
                      <span className="ffq-module-tag-name">{currentQuestion.type}</span>
                    </div>
                  ) : null}

                  <div className="ffq-question-content">
                    <div className="ffq-question-text">
                      <span className="ffq-question-num">{moduleQIndex + 1}.</span>
                      <span className="ffq-question-label">{currentQuestion.title}</span>
                    </div>

                    {currentQuestion.note && (
                      <div className="ffq-question-note">
                        <ReactMarkdown>{currentQuestion.note}</ReactMarkdown>
                      </div>
                    )}

                    {currentQuestion.reminder && (
                      <div className="ffq-question-note">
                        <ReactMarkdown>{currentQuestion.reminder}</ReactMarkdown>
                      </div>
                    )}

                    {currentQuestion.questionType === 1 ? (
                      <div className="ffq-options">
                        {(currentQuestion.options || []).map((opt) => {
                          const selected = answers[currentQuestion.id]?.optionId === opt.id;
                          return (
                            <Radio
                              key={opt.id}
                              className="ffq-option-radio"
                              checked={selected}
                              disabled={submitted}
                              onChange={() =>
                                setAnswerWithClear((prev) => ({
                                  ...prev,
                                  [currentQuestion.id]: { optionId: opt.id },
                                }))
                              }
                            >
                              <span className="ffq-option-text">
                                {opt.letter}. {opt.content}
                              </span>
                            </Radio>
                          );
                        })}
                      </div>
                    ) : currentQuestion.questionType === 3 || currentQuestion.questionType === 9 ? (
                      <div className="ffq-options">
                        {(currentQuestion.options || []).map((opt) => {
                          const selectedIds = answers[currentQuestion.id]?.optionIds || [];
                          const checked = selectedIds.includes(opt.id);
                          const maxReached =
                            currentQuestion.questionType === 3 && selectedIds.length >= 2;
                          return (
                            <Checkbox
                              key={opt.id}
                              className="ffq-option-radio"
                              checked={checked}
                              disabled={submitted || (!checked && maxReached)}
                              onChange={(e) => {
                                const next = e.target.checked
                                  ? [...selectedIds, opt.id]
                                  : selectedIds.filter((id) => id !== opt.id);
                                userMadeAnswerChangeRef.current = true;
                                setAnswers((prev) => {
                                  if (!next.length) {
                                    const cleaned = { ...prev };
                                    delete cleaned[currentQuestion.id];
                                    return cleaned;
                                  }
                                  return {
                                    ...prev,
                                    [currentQuestion.id]: { optionIds: next },
                                  };
                                });
                              }}
                            >
                              <span className="ffq-option-text">
                                {opt.letter}. {opt.content}
                              </span>
                            </Checkbox>
                          );
                        })}
                      </div>
                    ) : (
                      <textarea
                        className="ffq-textarea"
                        placeholder={t('ff.questionnaire.textPlaceholder')}
                        disabled={submitted}
                        value={answers[currentQuestion.id]?.textAnswer || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          userMadeAnswerChangeRef.current = true;
                          setAnswers((prev) => {
                            if (val) return { ...prev, [currentQuestion.id]: { textAnswer: val } };
                            const next = { ...prev };
                            delete next[currentQuestion.id];
                            return next;
                          });
                        }}
                      />
                    )}

                    {isValidationMode && (
                      <ValidationQuestionBody
                        questionId={currentQuestion.id}
                        validationField={getValidationField(currentQuestion.id)}
                        fileRequired={(validationData?.currentRound ?? 1) < 2}
                        editable={validationData?.editable ?? true}
                        answer={
                          validationAnswers[currentQuestion.id] || {
                            textEvidence: '',
                            remark: '',
                            files: [],
                          }
                        }
                        onUpdateAnswer={(field, value) =>
                          updateValidationAnswer(currentQuestion.id, field, value)
                        }
                        onUpload={handleValidationUpload(currentQuestion.id)}
                        beforeUpload={beforeUpload}
                        t={t}
                      />
                    )}
                  </div>

                  <div className="ffq-nav-btns">
                    <button
                      className="ffq-nav-btn"
                      onClick={() => navigateTo(visibleQuestions[globalIndex - 1])}
                      disabled={globalIndex <= 0}
                    >
                      {t('ff.questionnaire.prevQuestion')}
                    </button>
                    <button
                      className="ffq-nav-btn ffq-nav-btn--next"
                      onClick={() => navigateTo(visibleQuestions[globalIndex + 1])}
                      disabled={globalIndex >= visibleQuestions.length - 1}
                    >
                      {t('ff.questionnaire.nextQuestion')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {currentModuleObj?.content ? (
            <div className="ffq-card-content-right">
              <div className="ffq-hint-panel">
                <ReactMarkdown>{currentModuleObj.content}</ReactMarkdown>
              </div>
            </div>
          ) : null}
        </div>

        {/* Bottom bar */}
        {isValidationMode ? (
          <div className="ffq-bottom-bar ffq-bottom-bar--validation">
            <div className="ffq-bottom-bar-row ffq-bottom-bar-row--actions">
              <span className="ffq-progress-text">
                {t('ff.questionnaire.validation.uploadedCount')}
                <span className="ffq-progress-answered">{validationUploadedCount}</span>
                <span className="ffq-progress-total"> / {validationData?.totalDrawn ?? 0}</span>
              </span>
              <div className="ffq-bottom-actions">
                <button
                  className="ffq-btn-submit"
                  onClick={handleValidationSubmit}
                  disabled={isValidationSubmitting || validationData?.editable === false}
                >
                  {t('ff.questionnaire.validation.submit')}
                </button>
                <button
                  className="ffq-btn-save-progress"
                  onClick={handleValidationSaveDraft}
                  disabled={isValidationSubmitting || isValidationSaving}
                >
                  {t('ff.questionnaire.validation.saveDraft')}
                </button>
              </div>
            </div>
            <div className="ffq-bottom-bar-row">
              <div className="ffq-validation-info-bar">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="ffq-validation-info-svg"
                >
                  <path
                    d="M8.00067 0.666626C12.0505 0.666978 15.3337 3.95074 15.3337 8.00061C15.3333 12.0502 12.0502 15.3333 8.00067 15.3336C3.9508 15.3336 0.667039 12.0504 0.666687 8.00061C0.666687 3.95052 3.95058 0.666626 8.00067 0.666626ZM8.00067 2.00061C4.68696 2.00061 2.00067 4.6869 2.00067 8.00061C2.00102 11.314 4.68718 14.0006 8.00067 14.0006C11.3139 14.0003 14.0003 11.3138 14.0007 8.00061C14.0007 4.68712 11.3141 2.00096 8.00067 2.00061ZM8.66669 10.6666H9.66669V11.9996H6.33368V10.6666H7.33368V7.33362H6.66669V5.99963H8.66669V10.6666ZM8.00067 3.66663C8.4605 3.66698 8.8335 4.03976 8.83368 4.49963C8.83368 4.95965 8.46061 5.33327 8.00067 5.33362C7.54043 5.33362 7.16669 4.95987 7.16669 4.49963C7.16686 4.03955 7.54054 3.66663 8.00067 3.66663Z"
                    fill="#888888"
                  />
                </svg>
                <span className="ffq-validation-info-text">
                  {t('ff.questionnaire.validation.infoText')}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="ffq-bottom-bar">
            <span className="ffq-progress-text">
              {t('ff.questionnaire.answeredCount')}
              <span className="ffq-progress-answered">{answeredCount}</span>{' '}
              <span className="ffq-progress-total">/ {visibleQuestions.length}</span>
            </span>
            <div className="ffq-bottom-actions">
              <button className="ffq-btn-submit" onClick={handleSubmit} disabled={submitted}>
                {t('ff.questionnaire.submit')}
              </button>
              <button className="ffq-btn-draft" onClick={handleSaveDraft} disabled={submitted}>
                {t('ff.questionnaire.saveDraft')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Normal questionnaire modals */}

      <ErrorModal
        open={showIncompleteModal}
        onClose={() => setShowIncompleteModal(false)}
        title={t('ff.questionnaire.incomplete.title')}
        desc1={t('ff.questionnaire.incomplete.desc1')}
        desc2={t('ff.questionnaire.incomplete.desc2')}
        fieldTitle={t('ff.questionnaire.incomplete.pending')}
        fieldContent={incompleteFields.join('、')}
        btnText={t('ff.questionnaire.incomplete.btn')}
      />

      {/* Validation fail modal */}
      <ErrorModal
        open={showValidationFailModal}
        onClose={() => setShowValidationFailModal(false)}
        title={t('ff.questionnaire.validation.submitFail.title')}
        desc1={t('ff.questionnaire.validation.submitFail.desc1')}
        desc2=""
        fieldTitle=""
        fieldContent=""
        btnText={t('ff.questionnaire.validation.submitFail.btn')}
      />

      {/* Validation incomplete modal */}
      <ErrorModal
        open={showValidationIncompleteModal}
        onClose={() => setShowValidationIncompleteModal(false)}
        title={t('ff.questionnaire.validation.incomplete.title')}
        desc1={t('ff.questionnaire.validation.incomplete.desc1')}
        desc2={t('ff.questionnaire.validation.incomplete.desc2')}
        fieldTitle={t('ff.questionnaire.validation.incomplete.pending')}
        fieldContent={validationIncompleteFields.join('、')}
        btnText={t('ff.questionnaire.validation.incomplete.btn')}
      />

      <NoticeModal
        open={showNoticeModal}
        onClose={() => setShowNoticeModal(false)}
        title={t('ff.questionnaire.notice.title')}
        btnText={t('ff.questionnaire.notice.btn')}
        data={questionnaireNoticeData[i18n.language] ?? questionnaireNoticeData.zh}
      />
    </div>
  );
}

// ── Validation question body ───────────────────────────────────────────────────

function ValidationQuestionBody({
  validationField,
  fileRequired = true,
  editable,
  answer,
  onUpdateAnswer,
  onUpload,
  beforeUpload,
  t,
}) {
  const { validationStatus, materialRequirement, failReasonType, failReason, previousFiles } =
    validationField;
  const readonly = !editable || validationStatus === 'passed';

  if (validationStatus === 'notDrawn') {
    return (
      <div className="ffq-validation-body">
        <div className="ffq-not-drawn-notice">{t('ff.questionnaire.validation.notDrawn')}</div>
      </div>
    );
  }

  return (
    <div className="ffq-validation-body">
      {/* 首轮上传材料（二次验证时展示，只读） */}
      {previousFiles && previousFiles.length > 0 && (
        <div className="ffq-validation-field">
          <label className="ffq-validation-label">
            <span className="ffq-required">*</span>
            {t('ff.questionnaire.validation.previousFilesLabel')}
          </label>
          <div className="ffq-file-list">
            {previousFiles.map((file) => {
              const isPdf = /\.pdf$/i.test(file.name);
              return (
                <div key={file.uid} className="ffq-file-item">
                  {isPdf ? (
                    <span className="ffq-file-icon ffq-file-icon--pdf">PDF</span>
                  ) : (
                    <svg
                      className="ffq-file-icon ffq-file-icon--img"
                      width="32"
                      height="32"
                      viewBox="0 0 32 32"
                      fill="none"
                    >
                      <rect width="32" height="32" rx="6" fill="#8B6FE8" />
                      <circle cx="11" cy="12" r="2.5" fill="white" />
                      <path d="M4 22l7-8 4.5 5.5 3.5-4 6 6.5H4z" fill="white" />
                    </svg>
                  )}
                  {file.url ? (
                    <a className="ffq-file-name" href={file.url} target="_blank" rel="noreferrer">
                      {file.name}
                    </a>
                  ) : (
                    <span className="ffq-file-name">{file.name}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 不通过原因（只读展示） */}
      {(failReasonType || failReason) && (
        <div className="ffq-validation-field">
          <label className="ffq-validation-label">
            <span className="ffq-required">*</span>
            {t('ff.questionnaire.validation.failReasonLabel')}
          </label>
          <div className="ffq-validation-fail-reason">
            {failReasonType && failReasonType !== 'other' && (
              <p className="ffq-validation-fail-type">
                {REJECT_TYPE_KEYS.includes(failReasonType)
                  ? t(`ff.questionnaire.validation.rejectType.${failReasonType}`)
                  : failReasonType}
              </p>
            )}
            {failReason && <pre className="ffq-validation-fail-text">{failReason}</pre>}
          </div>
        </div>
      )}

      {/* 材料要求 */}
      {materialRequirement && (
        <div className="ffq-validation-field">
          <label className="ffq-validation-label">
            <span className="ffq-required">*</span>
            {t('ff.questionnaire.validation.requirementLabel')}
          </label>
          <div className="ffq-validation-requirement">
            <pre className="ffq-validation-requirement-text">{materialRequirement}</pre>
          </div>
        </div>
      )}

      {/* 上传证明材料 */}
      <div className="ffq-validation-field">
        <label className="ffq-validation-label">
          {fileRequired && <span className="ffq-required">*</span>}
          {t('ff.questionnaire.validation.uploadLabel')}
        </label>
        <Upload
          accept=".pdf,.jpg,.jpeg,.png"
          maxCount={5}
          multiple
          fileList={answer.files}
          beforeUpload={beforeUpload}
          customRequest={onUpload}
          onChange={({ fileList }) => {
            const normalized = fileList.map((f) => ({
              ...f,
              url: f.url || f.response?.url || '',
            }));
            onUpdateAnswer('files', normalized);
          }}
          showUploadList={false}
          disabled={readonly}
          className="ffq-upload-area"
        >
          <div className="ffq-upload-zone">
            <div className="ffq-upload-zone-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 3v13M12 3L8.5 6.5M12 3l3.5 3.5"
                  stroke="#aaa"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M20 16v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3"
                  stroke="#aaa"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <p className="ffq-upload-zone-text">
              {t('ff.questionnaire.validation.uploadDragText')}
            </p>
            <p className="ffq-upload-zone-hint">{t('ff.questionnaire.validation.uploadHint')}</p>
            <span className="ffq-upload-zone-btn">
              {t('ff.questionnaire.validation.uploadBtn')}
            </span>
          </div>
        </Upload>
        {answer.files && answer.files.length > 0 && (
          <div className="ffq-file-list">
            {answer.files.map((file) => {
              const isPdf = /\.pdf$/i.test(file.name);
              const isUploading = file.status === 'uploading';
              const isError = file.status === 'error';
              const isDone = !isUploading && !isError;
              return (
                <div
                  key={file.uid}
                  className={[
                    'ffq-file-item',
                    isUploading ? 'ffq-file-item--uploading' : '',
                    isError ? 'ffq-file-item--error' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {isUploading && <span className="ffq-file-icon--spinner" />}
                  {isDone &&
                    (isPdf ? (
                      <span className="ffq-file-icon ffq-file-icon--pdf">PDF</span>
                    ) : (
                      <svg
                        className="ffq-file-icon ffq-file-icon--img"
                        width="32"
                        height="32"
                        viewBox="0 0 32 32"
                        fill="none"
                      >
                        <rect width="32" height="32" rx="6" fill="#8B6FE8" />
                        <circle cx="11" cy="12" r="2.5" fill="white" />
                        <path d="M4 22l7-8 4.5 5.5 3.5-4 6 6.5H4z" fill="white" />
                      </svg>
                    ))}
                  {isDone && file.url ? (
                    <a className="ffq-file-name" href={file.url} target="_blank" rel="noreferrer">
                      {file.name}
                    </a>
                  ) : (
                    <span className="ffq-file-name">{file.name}</span>
                  )}
                  {!readonly && (
                    <button
                      className="ffq-file-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        onUpdateAnswer(
                          'files',
                          answer.files.filter((f) => f.uid !== file.uid)
                        );
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                        <path
                          d="M7.5 3.5h5M2.5 6h15m-2.5 0-.625 9.375A.833.833 0 0113.542 16.5H6.458a.833.833 0 01-.833-.791L5 6"
                          stroke="currentColor"
                          strokeWidth="1.4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}
                  {(isUploading || isError) && (
                    <div className="ffq-file-progress">
                      <div className="ffq-file-progress-track" />
                      <div
                        className={`ffq-file-progress-fill${isError ? ' ffq-file-progress-fill--error' : ''}`}
                        style={{ width: isError ? '30%' : `${file.percent || 0}%` }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 备注 */}
      <div className="ffq-validation-field">
        <label className="ffq-validation-label">
          {t('ff.questionnaire.validation.remarkLabel')}
        </label>
        <input
          className="ffq-validation-input"
          placeholder={t('ff.questionnaire.validation.remarkPlaceholder')}
          value={answer.remark}
          onChange={(e) => onUpdateAnswer('remark', e.target.value)}
        />
      </div>
    </div>
  );
}

// ── Notice modal ──────────────────────────────────────────────────────────────

function NoticeModal({ open, onClose, title, btnText, data }) {
  return (
    <Modal
      open={open}
      footer={null}
      onCancel={onClose}
      centered
      width={900}
      className="ffq-info-modal"
      classNames={{ content: 'ffq-info-modal-content' }}
      modalRender={(modal) => (
        <div className="ffq-info-modal-wrap">
          {modal}
          {btnText && (
            <div className="ffq-info-modal-footer">
              <button className="ffq-info-modal-btn" onClick={onClose}>
                {btnText}
              </button>
            </div>
          )}
        </div>
      )}
    >
      <div className="ffq-info-modal-header">
        <h3 className="ffq-info-modal-title">{title}</h3>
        <FfqCloseBtn onClick={onClose} />
      </div>
      <div
        className="ffq-info-modal-background"
        style={{
          backgroundImage: `url("${process.env.PUBLIC_URL}/image/Group_67.png")`,
        }}
      />
      <div className="ffq-info-modal-scroll">
        <div className="ffq-info-modal-body">
          {data && (
            <div className="ffq-notice-markdown">
              <ReactMarkdown>{data}</ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

function FfqCloseBtn({ onClick, className }) {
  return (
    <button
      type="button"
      className={`ffq-close-btn${className ? ` ${className}` : ''}`}
      aria-label="close"
      onClick={onClick}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path
          d="M1 1L13 13M13 1L1 13"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
}
